/* Kumii first-party storefront tracker v3 — full-site + journeys.
 *
 * First-party (posts to kumii.ee via the Shopify App Proxy → the Kumii hub), so Brave /
 * ad-blockers / Safari / Firefox cannot block it. Captures, on EVERY page:
 *   page_view  — path + page-type + referrer (one per page load)
 *   scroll     — deepest scroll % (sent on page-leave)
 *   click      — links/buttons (label + destination path)
 *   add_to_cart, checkout_click — commerce funnel (unchanged from v1)
 *
 * JOURNEYS: events optionally carry an anonymous tab-session id `sid` — a random
 * string kept in sessionStorage (cleared when the tab closes; no cookie, no
 * cross-site, no PII). It is created ONLY after the visitor consents (Shopify
 * Customer Privacy), so non-consenting visitors still produce anonymous
 * scroll/click/pageview aggregates but no linked journey. The hub groups events by
 * `sid` + time to reconstruct each visit's path (land → scroll → click → exit).
 *
 * v3 (hub phase 4, M17): consenting visitors also carry `vid`, a random id kept in
 * localStorage so the hub can tell new from returning visitors (never created without
 * consent, removed if consent is withdrawn); the landing page_view carries its campaign
 * tags and the NAMES of any ad click ids in the URL (never their values); clicks carry
 * the Shopify section they happened in, and the scroll beacon the sections the visitor
 * saw; add_to_cart is sent only when Shopify accepted the add.
 *
 * Endpoint = same-origin App Proxy path (kumii-analytics app → kumii.rosamus.ee/apps/insights).
 */
(function () {
  "use strict";

  var ENDPOINT = "/apps/insights/collect";

  // ── page type + product handle (locale-prefix tolerant) ───────────────────
  function stripLocale(p) { return p.replace(/^\/[a-z]{2}(?=\/|$)/, ""); }
  function pageType() {
    var p = stripLocale(location.pathname) || "/";
    if (p === "/" || p === "") return "home";
    if (/^\/products\//.test(p)) return "product";
    if (/^\/collections\//.test(p)) return "collection";
    if (/^\/blogs\//.test(p)) return "blog";
    if (/^\/pages\//.test(p)) return "page";
    if (/^\/cart/.test(p)) return "cart";
    if (/^\/search/.test(p)) return "search";
    if (/^\/account/.test(p)) return "account";
    return "other";
  }
  // product handle from ANY path/href (locale-tolerant), "" if not a product URL.
  function handleFromPath(p) {
    p = stripLocale(p || "");
    if (!/^\/products\//.test(p)) return "";
    return p.replace(/^\/products\//, "").split(/[/?#]/)[0];
  }
  function productHandle() { return handleFromPath(location.pathname); }
  function toPath(href) {
    try { return new URL(href || "", location.href).pathname; } catch (e) { return href || ""; }
  }

  var PTYPE = pageType();
  var HANDLE = productHandle();

  // ── consent (Shopify Customer Privacy) ────────────────────────────────────
  function consentAllowed() {
    // Primary: the custom banner (kumii-cookie-consent.liquid) writes this flag on
    // Accept — synchronous and reliable, unlike Shopify's async customerPrivacy API.
    try {
      if (localStorage.getItem("kumii-cookie-consent") === "granted") return true;
    } catch (e) {}
    // Fallbacks: Shopify Customer Privacy API (if loaded).
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp) {
        if (typeof cp.analyticsProcessingAllowed === "function" && cp.analyticsProcessingAllowed()) return true;
        if (typeof cp.getTrackingConsent === "function" && cp.getTrackingConsent() === "yes") return true;
      }
    } catch (e) {}
    return false;
  }

  // ── anonymous tab-session id: reuse if present (consent was already given);
  //    only CREATE a new one after consent. So pages 2+ link synchronously even
  //    before the consent API finishes loading. ───────────────────────────────
  function sid() {
    try {
      var s = sessionStorage.getItem("kx_sid");
      if (s) return s;
      if (consentAllowed()) {
        s = "s" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
        sessionStorage.setItem("kx_sid", s);
        return s;
      }
    } catch (e) {}
    return "";
  }

  // ── returning-visitor id: consenting visitors only. Kept across visits in
  //    localStorage; deleted when the visitor declines (not while Shopify's
  //    consent API is still loading, or a returning visitor would look new). ──
  function vid() {
    try {
      if (!consentAllowed()) {
        if (localStorage.getItem("kumii-cookie-consent") === "denied") localStorage.removeItem("kx_vid");
        return "";
      }
      var v = localStorage.getItem("kx_vid");
      if (!v) {
        v = "v" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
        localStorage.setItem("kx_vid", v);
      }
      return v;
    } catch (e) { return ""; }
  }

  // ── beacon (adds page-type + session id to every event) ───────────────────
  function send(obj) {
    obj.pt = PTYPE;
    var s = sid();
    if (s) obj.sid = s;
    var v = vid();
    if (v) obj.vid = v;
    // Consent usually arrives AFTER the entry page_view (the banner is answered
    // mid-page), so that first beacon went out unlinked. The moment ANY later
    // event carries a sid, re-send the entry page_view once so the visit's
    // first page joins the journey. Verified live: the custom banner's
    // setTrackingConsent call never fires Shopify's visitorConsentCollected
    // event, so this must not depend on it — the scroll flush at page-leave
    // always calls send(), making this hook the guaranteed path. Skipping
    // page_view events here prevents an already-linked entry (returning
    // consented visitor) from re-triggering on its own send.
    if (s && obj.t !== "page_view") relinkEntry();
    try {
      var payload = JSON.stringify(obj);
      var blob = new Blob([payload], { type: "text/plain" });
      if (!navigator.sendBeacon || !navigator.sendBeacon(ENDPOINT, blob)) {
        fetch(ENDPOINT, {
          method: "POST", body: payload, keepalive: true, mode: "same-origin",
          headers: { "Content-Type": "text/plain" }
        });
      }
    } catch (e) { /* never break the storefront over a beacon */ }
  }

  // referrer: internal → path (shows nav within the site); external → host only
  function refValue() {
    try {
      if (!document.referrer) return "";
      var u = new URL(document.referrer);
      return u.host === location.host ? u.pathname : u.host;
    } catch (e) { return ""; }
  }

  // ── 1. page_view (every page) ─────────────────────────────────────────────
  // `re: 1` marks a relink duplicate of the entry page_view: journeys NEED it
  // (it is how the first page joins the session), but anonymous aggregates
  // (funnel view counts) must EXCLUDE it or the pageview counts twice.
  // The landing's campaign tags and which ad click ids its URL carries (names only),
  // so the hub can tell paid visits from organic ones.
  function param(name) {
    try { return new URLSearchParams(location.search).get(name) || ""; } catch (e) { return ""; }
  }
  function entryView(extra) {
    var pv = { t: "page_view", path: location.pathname, handle: HANDLE, ref: refValue() };
    var us = param("utm_source"), um = param("utm_medium"), uc = param("utm_campaign");
    if (us) pv.us = us.slice(0, 80);
    if (um) pv.um = um.slice(0, 80);
    if (uc) pv.uc = uc.slice(0, 80);
    var ck = ["gclid", "gbraid", "wbraid", "fbclid"].filter(function (n) { return !!param(n); });
    if (ck.length) pv.ck = ck;
    if (extra) pv.re = 1;
    return pv;
  }
  var entryLinked = !!sid();   // did the entry page_view carry a sid?
  var entryResent = false;
  function relinkEntry() {
    if (entryLinked || entryResent) return;
    entryResent = true;        // set BEFORE send() — it re-enters this path
    send(entryView(true));
  }
  send(entryView(false));

  // Kept as a fast path in case Shopify's consent event ever does fire; the
  // guards in relinkEntry make it safe alongside the send() hook.
  document.addEventListener("visitorConsentCollected", relinkEntry);

  // ── 2. add_to_cart (theme-agnostic /cart/add intercept) ───────────────────
  function extractHandle(d) {
    if (!d) return "";
    if (d.handle) return d.handle;
    if (d.product_handle) return d.product_handle;
    if (d.items && d.items[0] && d.items[0].handle) return d.items[0].handle;
    return "";
  }
  var origFetch = window.fetch;
  if (typeof origFetch === "function") {
    window.fetch = function (input) {
      var url = (typeof input === "string") ? input : (input && input.url) || "";
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/add/.test(url)) {
        p.then(function (res) {
          if (!res || !res.ok) return;   // Shopify refused the add (e.g. 422 sold out): no add_to_cart
          var fb = HANDLE;
          try {
            res.clone().json().then(function (data) {
              send({ t: "add_to_cart", handle: extractHandle(data) || fb, path: location.pathname });
            }).catch(function () {
              send({ t: "add_to_cart", handle: fb, path: location.pathname });
            });
          } catch (e) { send({ t: "add_to_cart", handle: fb, path: location.pathname }); }
        }).catch(function () {});
      }
      return p;
    };
  }

  // ── 3. checkout_click (site-wide) ─────────────────────────────────────────
  var lastCheckout = -9999;
  function checkoutOnce() {
    var now = (window.performance && performance.now) ? performance.now() : 0;
    if (now - lastCheckout < 1500) return;
    lastCheckout = now;
    send({ t: "checkout_click", path: location.pathname });
  }

  // ── 4. click tracking (links + buttons → journey detail) ──────────────────
  // The Shopify section an element sits in, by its key in the template
  // ("shopify-section-template--123__kumii_hero_main" → "kumii_hero_main").
  function sectionKey(el) {
    var s = el && el.closest ? el.closest('[id^="shopify-section-"]') : null;
    if (!s) return "";
    var id = s.id.slice("shopify-section-".length);
    var i = id.lastIndexOf("__");
    return (i >= 0 ? id.slice(i + 2) : id).slice(0, 40);
  }
  document.addEventListener("click", function (e) {
    try {
      var el = e.target.closest("a, button, [role='button']");
      if (!el) return;
      if (el.matches('[name="checkout"], a[href*="/checkout"]')) checkoutOnce();
      var label = (el.getAttribute("aria-label") || el.textContent || "")
        .trim().replace(/\s+/g, " ").slice(0, 60);
      var href = el.getAttribute("href") || "";
      if (href) href = toPath(href);
      // product-link clicks (e.g. collection tiles) carry the handle as target so
      // tile clicks are attributable per product; everything else keeps its label.
      var tileHandle = handleFromPath(href);
      var click = { t: "click", path: location.pathname, target: tileHandle || label || el.tagName.toLowerCase(), href: href };
      var sec = sectionKey(el);
      if (sec) click.sec = sec;
      send(click);
      // Consent almost always arrives via the banner's Accept CLICK, but this
      // capture-phase listener runs BEFORE the banner's handler writes the
      // consent flag — so the click's own send() sees no sid. Re-check one tick
      // later: by then the flag is written, sid() can mint the id, and the
      // entry page_view relinks WHILE THE PAGE IS STILL OPEN. Without this the
      // relink waits for the leave-time scroll flush, and those two beacons
      // race through the collector's parallel workers — the relink can be
      // stamped AFTER the scroll, splitting one visit into two fake pages.
      if (!entryLinked) setTimeout(function () { if (sid()) relinkEntry(); }, 0);
    } catch (_) {}
  }, true);
  document.addEventListener("submit", function (e) {
    try { if (e.submitter && e.submitter.name === "checkout") checkoutOnce(); } catch (_) {}
  }, true);

  // ── 5. scroll depth (every page, sent on leave) ───────────────────────────
  var maxPct = 0, scrollSent = false, pageH = 1;
  function measure() {
    var doc = document.documentElement;
    var scrolled = (window.scrollY || doc.scrollTop || 0) + window.innerHeight;
    var height = Math.max(doc.scrollHeight, document.body.scrollHeight) || 1;
    pageH = height;
    var pct = Math.round((scrolled / height) * 100);
    if (pct > maxPct) maxPct = Math.min(100, pct);
  }
  // Bottom edge of the MAIN add-to-cart, in absolute document pixels. Null on
  // any page without one. Read at flush time so lazy-loaded layout has settled.
  //
  // A PDP carries ~17 [name="add"] buttons: the main buy box, the sticky bar,
  // and one per related-product card. Only the main one is a fold landmark, so
  // scope to .main-product-form and skip anything fixed-position (the sticky
  // bar's document offset is meaningless) or zero-height (hidden variant rows —
  // present inside the main form too, hence scanning for the first laid-out
  // candidate rather than trusting querySelector's first match).
  function atcPos() {
    try {
      // Must NOT fall back to document: a collection page carries ~18 [name="add"]
      // buttons from its product cards, and treating one of those as the fold
      // landmark would invent a "buy box" for a page that has none.
      var scope = document.querySelector('.main-product-form');
      if (!scope) return null;
      var els = scope.querySelectorAll('[name="add"]');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (el.closest && el.closest('sticky-atc-bar-minimalist')) continue;
        var r = el.getBoundingClientRect();
        if (!r.height) continue;
        return Math.round(r.bottom + (window.scrollY || 0));
      }
      return null;
    } catch (_) { return null; }
  }
  function flushScroll() {
    if (scrollSent) return;
    scrollSent = true;
    measure();
    var payload = { t: "scroll", path: location.pathname, handle: HANDLE,
                    max: maxPct, ph: pageH, vh: window.innerHeight };
    var a = atcPos();
    if (a !== null) payload.atc = a;
    if (seenSections.length) payload.secs = seenSections.slice(0, 40);
    send(payload);
  }
  // Section reach: the sections whose top came into the upper three quarters of the
  // screen, in the order seen (a margin, not a ratio, so very tall sections count too).
  var seenSections = [];
  try {
    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var k = sectionKey(en.target);
          if (k && seenSections.indexOf(k) < 0) seenSections.push(k);
          io.unobserve(en.target);
        });
      }, { rootMargin: "0px 0px -25% 0px", threshold: 0 });
      var observeSections = function () {
        var els = document.querySelectorAll('[id^="shopify-section-"]');
        for (var j = 0; j < els.length; j++) io.observe(els[j]);
      };
      if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", observeSections);
      else observeSections();
    }
  } catch (e) { /* never break the storefront over a beacon */ }
  measure();
  window.addEventListener("scroll", measure, { passive: true });
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") flushScroll();
  });
  window.addEventListener("pagehide", flushScroll);

  // ── 6. collection tile impressions (one beacon per collection pageview) ─────
  // The grid is server-rendered, so on a collection page we read the ordered list
  // of product handles shown (parsed from grid /products/ links → theme-agnostic).
  // Order = grid order, so the Pi can compute per-tile clicks-per-impression and
  // average position, and flag dead-zone tiles (impressions but 0 clicks).
  if (PTYPE === "collection") {
    try {
      var grid = document.querySelector(
        "#main-collection-product-grid, #ProductGridContainer, .collection .product-grid, .collection-grid, main"
      ) || document;
      var seen = {}, tiles = [], anchors = grid.querySelectorAll("a[href*='/products/']");
      for (var i = 0; i < anchors.length && tiles.length < 50; i++) {
        var h = handleFromPath(toPath(anchors[i].getAttribute("href") || ""));
        if (h && !seen[h]) { seen[h] = 1; tiles.push(h); }
      }
      if (tiles.length) send({ t: "collection_view", path: location.pathname, tiles: tiles });
    } catch (e) { /* never break the storefront over a beacon */ }
  }

  // ── 7. Ad click ids (Google gclid / Meta fbclid) → Shopify cart attributes ──
  // Consent-robust conversion path. Both networks' own conversion tracking is blind:
  // Google's was a GA4 import and Meta's is the pixel, and the cookie banner gates
  // BOTH for ~93% of visitors. So instead we persist the ad click id ourselves and
  // stamp it onto the Shopify cart, where it lands on order.note_attributes and the
  // hub can join real orders back to real ad clicks. No GA4, no pixel, no third-party
  // cookie — cannot be consent-gated away.
  // NB: intentionally NOT gated on consentAllowed() — gating it would reintroduce the
  // exact ~7% capture problem this exists to solve. Instead the cart also carries
  // `ad_consent` (yes/no, re-stamped when the answer changes), and the hub reports a
  // purchase back to Google or Meta only for a visitor who said yes (Allan, 2026-10-26).
  (function captureClickIds() {
    var TTL_MS = 90 * 24 * 60 * 60 * 1000; // click-through conversion window (90d)

    // Resolve one network's click id: take it fresh off the URL if this is an ad
    // landing, else fall back to the stored one. Last click wins, per network.
    // `k` remembers which parameter it came in as (gclid, gbraid or wbraid).
    function resolve(storeKey, names) {
      var fresh = "", key = "";
      for (var i = 0; i < names.length && !fresh; i++) { fresh = param(names[i]); key = names[i]; }
      var store = null;
      try { store = JSON.parse(localStorage.getItem(storeKey) || "null"); } catch (e) {}
      if (fresh) {
        store = { v: fresh, t: Date.now(), k: key };
        try { localStorage.setItem(storeKey, JSON.stringify(store)); } catch (e) {}
      }
      if (store && (Date.now() - (store.t || 0) > TTL_MS)) {   // expire stale id
        store = null;
        try { localStorage.removeItem(storeKey); } catch (e) {}
      }
      return (store && store.v) ? store : null;
    }

    // gclid = standard Google; gbraid/wbraid = iOS/app web-click ids, stamped under
    // their own names because Google takes each through a different field (ids stored
    // before v3 have no `k` and are stamped as gclid). fbclid = Meta (Facebook + Instagram).
    var g = resolve("kx_gclid", ["gclid", "gbraid", "wbraid"]);
    var f = resolve("kx_fbclid", ["fbclid"]);
    if (!g && !f) return;

    var attrs = {};
    if (g) {
      var gk = g.k || "gclid";
      ["gclid", "gbraid", "wbraid"].forEach(function (n) {   // an empty value removes an older one
        attrs[n] = n === gk ? g.v : "";
        attrs[n + "_ts"] = n === gk ? String(g.t) : "";
      });
    }
    if (f) { attrs.fbclid = f.v; attrs.fbclid_ts = String(f.t); }
    attrs.ad_consent = consentAllowed() ? "yes" : "no";

    // BOTH ids are stamped when both exist, together with their timestamps. A visitor
    // can click a Google ad and later a Facebook one; the order must not be credited
    // to both, so the hub compares the timestamps and credits the LATER click only.
    // Dropping one here instead would throw away the evidence needed to decide.
    var sig = (g ? g.v : "") + "|" + (f ? f.v : "") + "|" + attrs.ad_consent;
    try { if (sessionStorage.getItem("kx_clickid_stamped") === sig) return; } catch (e) {}
    try {
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ attributes: attrs })
      }).then(function () {
        try { sessionStorage.setItem("kx_clickid_stamped", sig); } catch (e) {}
      }).catch(function () {});
    } catch (e) { /* never break the storefront */ }
  })();
})();
